import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { getContactInfo } from '@sbt-web/network/client';
import { type AdItem } from '@sbt-web/network/types';
import { Button, ButtonSize, ButtonDesign, Icon, IconSize, ShortModal } from '@sbt-web/ui';
import { HADES_PATH } from '@/shared/constants';
import { phoneCallGTMEvent, phoneContactEvent, showPhoneContactGTMEvent } from '../../../tracking';
import type { SearchData } from '../../../types';
import classes from './index.module.scss';
export type PulseEvent = {
  name: string;
  pageType: 'ad_detail' | 'listing';
};
export type PhoneButtonProps = {
  isMobile: boolean;
  adUrn: string;
  item: AdItem;
  search?: SearchData;
  buttonSize: ButtonSize;
  buttonDesign: ButtonDesign;
  buttonLabelDesktop?: string;
  buttonLabelMobile?: string;
  buttonShowIcon?: boolean;
  buttonClass?: string;
  pulseEvent: PulseEvent;
};
interface PhoneButtonDialogProps {
  phoneNumber: string;
  dialogOpened: boolean;
  handleClose: () => void;
}
export const PhoneButtonDialog = ({
  phoneNumber,
  dialogOpened,
  handleClose
}: PhoneButtonDialogProps) => {
  const copyContent = async () => {
    try {
      await navigator.clipboard.writeText(phoneNumber);
    } catch (err) {
      console.error('Failed to copy: ', err);
    } finally {
      handleClose();
    }
  };
  return <ShortModal title="Numero di telefono" ariaDescription="Numero di telefono" open={dialogOpened} onClose={handleClose} buttonConfiguration={{
    main: {
      action: () => {
        copyContent();
      },
      text: 'Copia',
      design: 'solid'
    }
  }}>
      <address className={classes.number}>{phoneNumber}</address>
    </ShortModal>;
};
export const PhoneButton = t0 => {
  const $ = _c(20);
  const {
    isMobile,
    adUrn,
    search,
    item,
    buttonDesign,
    buttonSize,
    buttonLabelDesktop,
    buttonLabelMobile,
    buttonShowIcon: t1,
    buttonClass,
    pulseEvent
  } = t0;
  const buttonShowIcon = t1 === undefined ? true : t1;
  const {
    phoneNumber,
    dialogOpened,
    handleMobileClick,
    handleDesktopClick,
    closeDialog
  } = usePhoneNumberHandler(adUrn, item, pulseEvent, search);
  let buttonLabel = null;
  if (buttonLabelMobile && buttonLabelDesktop) {
    buttonLabel = isMobile ? buttonLabelMobile : buttonLabelDesktop;
  }
  let t2;
  if ($[0] !== closeDialog || $[1] !== dialogOpened || $[2] !== isMobile || $[3] !== phoneNumber) {
    t2 = !isMobile && <PhoneButtonDialog dialogOpened={dialogOpened} phoneNumber={phoneNumber ?? ""} handleClose={closeDialog} />;
    $[0] = closeDialog;
    $[1] = dialogOpened;
    $[2] = isMobile;
    $[3] = phoneNumber;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = isMobile ? handleMobileClick : handleDesktopClick;
  let t4;
  if ($[5] !== buttonShowIcon) {
    t4 = buttonShowIcon ? <Icon name="Phone" iconSize={IconSize.MD} /> : undefined;
    $[5] = buttonShowIcon;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = buttonClass ?? "";
  let t6;
  if ($[7] !== t5) {
    t6 = [classes.phoneButton, t5];
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = isMobile ? "Chiama" : "Mostra numero di telefono";
  let t8;
  if ($[9] !== buttonDesign || $[10] !== buttonLabel || $[11] !== buttonSize || $[12] !== t3 || $[13] !== t4 || $[14] !== t6 || $[15] !== t7) {
    t8 = <Button design={buttonDesign} onClick={t3} size={buttonSize} icon={t4} classes={t6} aria-label={t7} slim={true}>{buttonLabel}</Button>;
    $[9] = buttonDesign;
    $[10] = buttonLabel;
    $[11] = buttonSize;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t2 || $[18] !== t8) {
    t9 = <>{t2}{t8}</>;
    $[17] = t2;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
const usePhoneNumberHandler = (adUrn, item, pulseEvent, search) => {
  const $ = _c(22);
  const [phoneNumber, setPhoneNumber] = useState();
  const [dialogOpened, setDialogOpened] = useState(false);
  let t0;
  if ($[0] !== adUrn || $[1] !== item || $[2] !== phoneNumber || $[3] !== pulseEvent.name || $[4] !== pulseEvent.pageType || $[5] !== search) {
    t0 = async () => {
      window.dispatchEvent(new CustomEvent("subito:PhoneCall", {
        detail: search
      }));
      phoneContactEvent({
        item,
        search,
        type: "Call",
        name: pulseEvent.name,
        pageType: pulseEvent.pageType
      });
      phoneCallGTMEvent();
      if (phoneNumber) {
        window.location.href = `tel:${phoneNumber}`;
        return;
      }
      try {
        const {
          body
        } = await getContactInfo(HADES_PATH, adUrn);
        const phoneNumber_0 = body.phoneNumber;
        if (phoneNumber_0) {
          setPhoneNumber(phoneNumber_0);
          window.location.href = `tel:${phoneNumber_0}`;
        }
      } catch {
        captureException(new Error(`Error while trying to get phone number for ad ${adUrn} on mobile`));
      }
    };
    $[0] = adUrn;
    $[1] = item;
    $[2] = phoneNumber;
    $[3] = pulseEvent.name;
    $[4] = pulseEvent.pageType;
    $[5] = search;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const handleMobileClick = t0;
  let t1;
  if ($[7] !== item || $[8] !== pulseEvent.name || $[9] !== pulseEvent.pageType || $[10] !== search) {
    t1 = (phoneNumber_1, adUrn_0) => {
      window.dispatchEvent(new CustomEvent("subito:ShowPhoneContact", {
        detail: {
          phoneNumber: phoneNumber_1,
          urn: adUrn_0,
          search
        }
      }));
      showPhoneContactGTMEvent();
      phoneContactEvent({
        item,
        search,
        type: "Show",
        name: pulseEvent.name,
        pageType: pulseEvent.pageType
      });
    };
    $[7] = item;
    $[8] = pulseEvent.name;
    $[9] = pulseEvent.pageType;
    $[10] = search;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const dispatchShowPhoneContactEvent = t1;
  let t2;
  if ($[12] !== adUrn || $[13] !== dispatchShowPhoneContactEvent || $[14] !== phoneNumber) {
    t2 = async () => {
      if (phoneNumber) {
        dispatchShowPhoneContactEvent(phoneNumber, adUrn);
        setDialogOpened(true);
        return;
      }
      try {
        const {
          body: body_0
        } = await getContactInfo(HADES_PATH, adUrn);
        const {
          adURN,
          phoneNumber: phoneNumber_2
        } = body_0;
        if (phoneNumber_2) {
          dispatchShowPhoneContactEvent(phoneNumber_2, adURN);
          setPhoneNumber(phoneNumber_2);
          setDialogOpened(true);
        }
      } catch {
        captureException(new Error(`Error while trying to get phone number for ad ${adUrn} on desktop`));
      }
    };
    $[12] = adUrn;
    $[13] = dispatchShowPhoneContactEvent;
    $[14] = phoneNumber;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const handleDesktopClick = t2;
  let t3;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setDialogOpened(false);
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== dialogOpened || $[18] !== handleDesktopClick || $[19] !== handleMobileClick || $[20] !== phoneNumber) {
    t4 = {
      phoneNumber,
      dialogOpened,
      handleMobileClick,
      handleDesktopClick,
      closeDialog: t3
    };
    $[17] = dialogOpened;
    $[18] = handleDesktopClick;
    $[19] = handleMobileClick;
    $[20] = phoneNumber;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};